import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Avatar from 'flarum/common/components/Avatar';

/**
 * v0.8.10 — 教练色板 (辉哥 10/7 13:03 报需求, 13:18 拍板 A 方案)
 *
 * ⚠️ 前后端共用同一份常量: 这 22 个 hex 必须跟
 *   packages/ziven-scheduler/src/Helpers/CoachColorPalette.php 的 PALETTE 完全一致,
 *   以及 CalendarTab.js 的 COACH_COLOR_MAP、less/forum.less 的 .zs-ev-c-* (按下面的 name)。
 *   不一致的后果: 后端挑出前端 COACH_COLOR_MAP 里没有的颜色 → CalendarTab 映射不到
 *   → 事件块静默回落到教室色, 表现为"改了颜色没反应"。
 *
 * v0.10.1 — 10 色扩到 22 色 (辉哥 10/8 要求"可分配的颜色再多一些")。
 *   扩充原则:
 *     ① 新增色按色相铺开 (天蓝/蓝绿/草绿/橄榄/金黄/品红/深紫/玫红/深青/深绿/淡紫/棕铜),
 *        尽量落在原有 10 色没占的色相段, 而不是再堆近似色;
 *     ② 少数同色相深浅成对 (深青/青、草绿/绿、深绿/绿、淡紫/深紫) 是有意保留的
 *        —— 深浅两档在色盘里并排一眼可辨, 且能覆盖"同校区人特别多"的场景;
 *     ③ **前 10 个顺序一律不动** —— 后端 CoachColorPalette::pickUnused 按 PALETTE 顺序
 *        取"第一个空位", 顺序动了会让未手动选色的新教练拿到跟历史不同的颜色。
 *
 * 为什么用固定色板而不是 <input type="color"> 让用户自由取色:
 *   自由取色能出 #3f7a12 这种在深色课表背景上看不清的脏色, 而且没法保证同校区不撞色。
 *   固定色板 + 后端排除已用色 (CoachColorPalette::pickUnused) 才是验收第 2 条要的效果。
 */
export const COACH_PALETTE = [
// ── 原 10 色 (顺序不变, 见上文原则 ③) ──
'var(--zs-brand-2d7be5, #2d7be5)',
// 蓝
'var(--zs-warning-f59e0b, #f59e0b)',
// 橙
'var(--zs-success-10b981, #10b981)',
// 绿
'var(--zs-purple-8b5cf6, #8b5cf6)',
// 紫
'var(--zs-danger-ef4444, #ef4444)',
// 红
'var(--zs-cyan-06b6d4, #06b6d4)',
// 青
'var(--zs-pink-ec4899, #ec4899)',
// 粉
'var(--zs-success-84cc16, #84cc16)',
// 黄绿
'var(--zs-orange-f97316, #f97316)',
// 深橙
'var(--zs-indigo-6366f1, #6366f1)',
// 靛
// ── v0.10.1 新增 12 色 ──
'var(--zs-info-0ea5e9, #0ea5e9)',
// 天蓝
'var(--zs-cyan-14b8a6, #14b8a6)',
// 蓝绿
'var(--zs-success-22c55e, #22c55e)',
// 草绿
'var(--zs-success-65a30d, #65a30d)',
// 橄榄
'var(--zs-warning-eab308, #eab308)',
// 金黄
'var(--zs-pink-d946ef, #d946ef)',
// 品红
'var(--zs-purple-7c3aed, #7c3aed)',
// 深紫
'var(--zs-danger-e11d48, #e11d48)',
// 玫红
'var(--zs-cyan-0891b2, #0891b2)',
// 深青
'var(--zs-success-059669, #059669)',
// 深绿
'var(--zs-purple-c084fc, #c084fc)',
// 淡紫
'var(--zs-danger-b45309, #b45309)' // 棕铜
];

/* ═══════════════════════════════════════════════════════════════════════════
 * v0.8.10a — 色盘 portal: 把 10 色色盘从"列表行里"搬到 document.body
 * ───────────────────────────────────────────────────────────────────────────
 * 【缺陷】v0.8.10 的色盘是 .ZS-assign-user-color 的绝对定位子元素
 *   (position:absolute; right:0; top:calc(100% + 6px); z-index:30)。
 *   色盘**只往下展开**, 在列表最后一行展开时, 第二排 5 个色点正好落进
 *   .Modal-footer 的盒子里 (实测: 色盘 y 683→751, footer y 732→828),
 *   footer 在 DOM 里排在后面且是不透明底 → 真点过去命中的元素是
 *   DIV.Modal-footer 而不是 BUTTON.ZS-assign-color-dot, 点色完全无效,
 *   而且色盘不会收起 → "无法恢复"。
 *   桌面 1280×900 实测最后一行只有 5/10 可点; 移动 390×844 更狠, 0/10。
 *
 * 【为什么选 B 而不是 A/C】
 *   A (空间不够就向上翻转): 还是留在 .Modal-content 里, 而 Flarum 的
 *     .Modal-content 是 overflow:hidden —— 列表很长时色盘在列表顶部展开
 *     会被内容框裁掉; 而且 resize/滚动后坐标会过期, 得反复重算。
 *   C (让列表可滚动 + 抬 z-index): 抬 z-index 抬不过 footer —— 两者是
 *     .Modal-content 下不同的绘制层, 靠抬子元素 z-index 压不过 footer;
 *     而且列表本来就有 max-height:360px/overflow-y:auto, 候选少时
 *     scrollHeight == clientHeight, 压根没有滚动空间可救。
 *   B (挂到 modal 层级): position:fixed + 挂 document.body, 直接脱离
 *     列表容器和 footer 的裁剪/层叠上下文, 所有行行为完全一致, 不用判断空间,
 *     顺带把"点外部关闭 / ESC 关闭"一起补上 (v0.8.10 都没有)。
 *
 * 【落点为什么用 fixed 而不是 absolute】
 *   Flarum 的 .Modal 带 transform: scale(1) (见 core less/common/Modal.less L8/L18),
 *   而带 transform 的祖先会成为 position:fixed 的包含块 —— 挂在 modal 内部的话
 *   坐标要按 modal 盒算, 移动端媒体查询又把 transform 置成 none (!important),
 *   两端行为不一致, 进出场动画期间 scale(0.9) 还会算错。挂到 body 上就没有这些问题
 *   (body/html 的 transform/filter 都是 none, body 也不会被锁定成 position:fixed,
 *   因为 Flarum 的 disableBodyScroll 锁的是 .ModalManager 而不是 body)。
 */

/** 色盘与锚点之间的间距 —— 跟 v0.8.10 段 top: calc(100% + 6px) 的 6px 保持一致 */
const PALETTE_GAP = 6;
/** 色盘贴视口边缘时保留的最小留白 */
const PALETTE_MARGIN = 8;

/**
 * 算色盘该放哪 (视口坐标, 给 position:fixed 用)
 *
 * @param {DOMRect} anchor 锚点 (色块) 的视口矩形
 * @param {{w:number,h:number}} size 色盘自身尺寸 (挂上去后实测, 不硬编码)
 * @param {{w:number,h:number}} viewport 视口尺寸
 */
function computePalettePos(anchor, size, viewport) {
  const below = viewport.h - anchor.bottom - PALETTE_MARGIN;
  const above = anchor.top - PALETTE_MARGIN;
  // 下方装不下、且上方更宽裕时向上翻 (改前只会向下, 所以最后一行必被 footer 压住)
  const flip = size.h + PALETTE_GAP > below && above > below;
  let top = flip ? anchor.top - PALETTE_GAP - size.h : anchor.bottom + PALETTE_GAP;
  let left = anchor.right - size.w; // 右边缘跟色块右边缘对齐 (跟改前 right:0 一致)
  // 两条兜底夹取: 无论翻没翻都不能溢出视口 (窄屏/列表顶部也要保证完整可见)
  top = Math.max(PALETTE_MARGIN, Math.min(top, viewport.h - size.h - PALETTE_MARGIN));
  left = Math.max(PALETTE_MARGIN, Math.min(left, viewport.w - size.w - PALETTE_MARGIN));
  return {
    left,
    top,
    flip
  };
}

/**
 * 造出挂到 document.body 上的 10 色盘 (纯 DOM, 不走 mithril 渲染)
 *
 * ⚠️ 为什么不用 m.render(vnode, dom) 起一个独立 root:
 *   Flarum 2 的全局 `m` 是被它自己包过一层 hyperscript 的 mithril, 实测在本项目的
 *   forum.js (v=a50165e8) 上 m.render(m('div','x'), div) 会直接抛
 *   "Cannot read properties of undefined (reading 'view')" —— 连最普通的字符串标签
 *   vnode 都渲染不了, 起独立 root 这条路在本运行时走不通。
 *   色盘本身是"固定 10 个色点"的静态小组件, 用 createElement 手搓反而最省事,
 *   也跟本项目已有的先例一致 (CalendarTab 的 ZS-ctxmenu 右键菜单就是纯 DOM 建的)。
 *
 * @param {object} opts
 *   currentHex  当前生效色 (决定哪个色点带 active 白圈), 没有则 null
 *   onPick      点色点回调 (hex)
 * @returns {{host: HTMLElement, palette: HTMLElement, dots: HTMLElement[]}}
 */
function buildPaletteDom(_ref) {
  let {
    currentHex,
    onPick
  } = _ref;
  const host = document.createElement('div');
  host.className = 'ZS-assign-color-palette-host';
  const palette = document.createElement('div');
  palette.className = 'ZS-assign-color-palette';
  const dots = COACH_PALETTE.map(hex => {
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'ZS-assign-color-dot' + (currentHex === hex ? ' active' : '');
    dot.style.background = hex;
    dot.title = hex;
    // ⚠️ stopPropagation: 色点在 body 上, 冒泡不会命中行; 留着是防止将来挪回 modal
    //   内时"点颜色 = 顺手勾选分配"这种隐蔽误操作 (v0.8.10 行内色盘就有这个坑)
    dot.addEventListener('click', e => {
      e.stopPropagation();
      onPick(hex);
    });
    palette.appendChild(dot);
    return dot;
  });
  host.appendChild(palette);
  return {
    host,
    palette,
    dots
  };
}

/**
 * v0.5.0v — 分配教练 / 销售 Modal
 *
 * 严格按设计图 /Users/zhaozihui/.mavis/agents/mavis/workspace/designs/assign-staff-modal.html
 *
 * Props:
 *   - school: 当前校区对象
 *   - onClose: 关闭后回调 (SchoolsTab 刷新数据)
 *
 * 数据流:
 *   1. 候选 user 拉自 /api/ziiven/scheduler/users?role=coach,sales (复用 ListUsersByRoleController)
 *   2. 已分配 user 拉自 /api/ziiven/scheduler/schools/{id}/staff (新建 ListSchoolStaffController)
 *   3. 提交: PUT /api/ziiven/scheduler/schools/{id}/staff { ops: [{user_id, action}] }
 *
 * 直接用 app.request (跟 utils/Api.js 同款, 避免 flarum/common/utils/Api 路径在 Flarum 2.0 不存在)
 */
export default class AssignStaffModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    // v0.6.48 — school 兜底从全局 activeSchoolId 读取 (辉哥 9/30 12:14+ 反馈)
    //   改前只接 caller 传的 school, 如果 caller 没传或 school 对象丢失 → this.school.id undefined
    //     → URL /schools/undefined/staff → 后端 422 "校区 id 缺失"
    //   改后优先 caller 传 school, 兜底从全局 dropdown (localStorage ziven_scheduler_active_school_id)
    //     任意一种能拿到都 OK, 都拿不到再 alert('请先选择校区') (load L62-63 兜底)
    //   兼容 SchoolsTab v0.6.48 L29 + 旧 caller 传 school, 不破坏现有行为
    const callerSchool = vnode.attrs.school;
    const globalActiveSchoolId = localStorage.getItem('ziven_scheduler_active_school_id');
    let resolvedSchool = callerSchool;
    if (!resolvedSchool && globalActiveSchoolId) {
      // 兜底全局 activeSchoolId: 包装成最小 school 对象 (id 必填, name 从 vnode.attrs.schools 找)
      resolvedSchool = {
        id: parseInt(globalActiveSchoolId, 10),
        name: null // title() L42-58 兜底从 this.attrs.schools 找 name
      };
    }
    // 同时存 this.schools 列表给 title() 用
    this.schools = vnode.attrs.schools || [];
    // v0.8.10 — 删了 '[AssignStaff DEBUG] oninit attrs=...' 这行 (辉哥 10/7 拍板: 清 console 噪声)
    //   这几行是 v0.6.48 排查 "school undefined" 时加的一次性 trace, 问题早已定位修完。
    this.school = resolvedSchool;
    this.onCloseCb = vnode.attrs.onClose;
    this.saving = false;
    this.error = '';
    this.activeRole = 'coach';
    this.search = '';
    this.candidates = [];
    this.assigned = [];
    this.checkedIds = new Set();
    // v0.8.10 — 颜色草稿: user_id → 选中的 hex
    //   为什么要有草稿层, 不直接改 this.assigned:
    //   用户在 modal 里点色块是"待保存"的中间态, 直接改 assigned 会让"保存分配"按钮
    //   在没有任何勾选变化时也认为有改动, 语义混乱。草稿在 load() 时用后端返回的
    //   coach_color 初始化, 保存时才随 ops 一起 PUT 给后端。
    this.colorDraft = {};
    // 哪个用户展开了取色盘 (同一时刻只展开一个, 避免每行都摊开 10 个色点把 modal 撑爆)
    this.openPaletteUserId = null;
    // v0.8.10a — 色盘 portal 状态: 挂在 body 上的纯 DOM 容器 + 定位用的锚点 + 跟随监听
    //   paletteHost / paletteEl: body 上的容器与其中的色盘节点 (v0.8.10a 新增; 改前色盘在行内)
    //   paletteAnchorEl:        锚点 (那一行的色块), 滚动/resize 后按它实时重算坐标
    //   监听: 点色盘外部关闭 / ESC 关闭 (v0.8.10 两者都没有, 只能再点一次色块收起)
    this.paletteHost = null;
    this.paletteEl = null;
    this.paletteAnchorEl = null;
    this.onPaletteViewportChange = null;
    this.onDocPointerDown = e => {
      if (this.openPaletteUserId === null || !this.paletteHost) return;
      // 点色盘内部 / 点色块本体 → 不在这里关 (色块自己 toggle, 否则会先关后开的竞态)
      if (e.target.closest && e.target.closest('.ZS-assign-color-palette, .ZS-assign-color-swatch')) return;
      this.closePalette();
    };
    this.onDocKeyDown = e => {
      if (this.openPaletteUserId === null || e.key !== 'Escape') return;
      // preventDefault 用来吃掉这次 ESC: Flarum 2 的 ModalManager 用 CloseWatcher 收 ESC,
      // 不拦住的话按一次会连整个 modal 一起关掉 (改前就是这个行为)
      e.preventDefault();
      e.stopPropagation();
      this.closePalette();
    };
    this.loading = true;
  }
  className() {
    return 'Modal--large AssignStaffModal';
  }
  title() {
    // v0.6.48 — 兜底 school 可能没 name (caller 没传 school, 只传 activeSchoolId), 从 this.attrs.schools 找
    let schoolName = this.school?.name || '';
    if (!schoolName && this.school?.id && this.attrs && this.attrs.schools) {
      const s = this.attrs.schools.find(x => String(x.id) === String(this.school.id));
      if (s) schoolName = s.name;
    }
    return m("span", null, m("i", {
      className: "fas fa-people-arrows",
      style: "color:var(--zs-brand-2d7be5, #2d7be5);margin-right:6px;"
    }), "\u5206\u914D\u4EBA\u5458 \u2014 ", schoolName);
  }

  // v0.6.47 — Flarum 2.0 Component.oncreate 必须传 vnode 参数 (辉哥 9/30 11:17+ 反馈)
  //   改前 oncreate() { super.oncreate() } 没传 vnode, Component.tsx:70 `this.element = vnode.dom` t undefined 报错
  //   改后 super.oncreate(vnode) 透传 vnode, this.element 正确赋值
  //   (跟 v0.5.0a SOP 89 同类 mithril 2.0 lifecycle 升级后强制, 后续沉淀进 SOP 129 跨项目永久)
  //   改后 super.oncreate(vnode) 透传 vnode, this.element 正确赋值
  //   (跟 v0.5.0a SOP 89 同类 mithril 2.0 lifecycle 升级后强制, 后续沉淀进 SOP 129 跨项目永久)
  // v0.8.10 — 原本这里还有一行 '[AssignStaff DEBUG] oncreate...' 日志, 按辉哥 10/7 要求删除;
  //   super.oncreate(vnode) 这个修复本身保留, 动不得 (删了 this.element 会 undefined)。
  oncreate(vnode) {
    super.oncreate(vnode);
    // v0.8.10a — 点色盘外部 / ESC 关色盘的监听挂在 document 上 (色盘现在在 body 上, 不是 modal 子节点,
    //   挂在 modal 上收不到它内部的点击)。用 pointerdown 而不是 click: 在 mousedown 阶段就收手,
    //   不会和"点色块本体展开色盘"的 click 抢先后顺序。
    document.addEventListener('pointerdown', this.onDocPointerDown, true);
    document.addEventListener('keydown', this.onDocKeyDown, true);
    this.load();
  }

  // v0.8.10a — modal 关闭/卸载时必须把 portal 连同 document 监听一起清掉,
  //   否则色盘会变成"关不掉的孤儿"浮在页面上 (它已经不在 modal 的 vdom 里, mithril 不会帮你删)。
  onremove(vnode) {
    document.removeEventListener('pointerdown', this.onDocPointerDown, true);
    document.removeEventListener('keydown', this.onDocKeyDown, true);
    this.closePalette();
    super.onremove(vnode);
  }
  async load() {
    // v0.6.48 — 最后兜底: 如果 oninit 后 this.school 还是 undefined, alert + 不发请求
    //   链式: caller 没传 school + localStorage 没 activeSchoolId → this.school undefined
    //   之前会发请求到 /schools/undefined/staff → 后端 422 "校区 id 缺失"
    //   改后 load() 主动检查, 友好提示 + this.error 显示
    if (!this.school || !this.school.id) {
      console.warn('[AssignStaff] school id 缺失, 全局 activeSchoolId 也没设, 请先选择校区');
      this.error = '请先在顶部校区选择器选择校区';
      this.loading = false;
      m.redraw();
      return;
    }
    this.loading = true;
    m.redraw();
    try {
      // 1) 候选: 所有 coach + sales
      const all = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/users',
        params: {
          role: 'coach,sales,manager'
        }
      });
      // v0.8.10 — 删了 '[AssignStaff DEBUG] load: users 返回' 这行日志 (打印整个 users 数组,
      //   每次开 modal 都刷一屏, 是控制台噪声的主要来源之一)。辉哥 10/7 拍板删除。
      this.candidates = Array.isArray(all) ? all : [];
      // v0.6.63 — 推用户到 Flarum store, 让 Avatar 组件能拿到 user model
      //   ListUsersByRoleController 返手写 JSON (display_name/avatar_url), 不会自动 push 到 store
      //   Flarum User model attributes 用 camelCase JSON:API 名 (displayName/avatarUrl), 转换后 push
      //   pushPayload 后 app.store.getById('users', id) 拿到 User model → Avatar 渲染 <img> + color() hash
      app.store.pushPayload({
        data: this.candidates.map(u => ({
          type: 'users',
          id: String(u.id),
          attributes: {
            username: u.username,
            displayName: u.display_name,
            avatarUrl: u.avatar_url
          }
        }))
      });
      // 2) 已分配
      const staff = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/schools/' + this.school.id + '/staff'
      });
      const arr = Array.isArray(staff) ? staff : [];
      this.assigned = arr;
      this.checkedIds = new Set(arr.map(u => u.user_id));
      // v0.8.10 — 用后端返回的 coach_color 初始化颜色草稿 (ListSchoolStaffController v0.8.10 起返回该字段)
      //   只存**显式有值**的: 没配色的老数据是 null, 不写进草稿,
      //   这样"没动过颜色"和"颜色是空"在提交时行为一致 (交给后端按色板自动挑)。
      this.colorDraft = {};
      arr.forEach(u => {
        if (u.coach_color) this.colorDraft[u.user_id] = u.coach_color;
      });
    } catch (e) {
      console.error('[AssignStaff] load failed', e);
      this.error = '加载失败: ' + (e?.message || '');
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
  toggleUser(userId) {
    if (this.checkedIds.has(userId)) this.checkedIds.delete(userId);else this.checkedIds.add(userId);
    m.redraw();
  }
  filteredUsers() {
    const keyword = this.search.trim().toLowerCase();
    return this.candidates.filter(u => u.role === this.activeRole).filter(u => !keyword || (u.username || '').toLowerCase().includes(keyword) || (u.display_name || '').toLowerCase().includes(keyword) || (u.email || '').toLowerCase().includes(keyword));
  }

  /**
   * v0.8.10 — 这一行当前生效的颜色 (草稿优先, 回落到后端已存值), 没有则 null
   *   null 的含义 = 还没定色 → 未分配的人保存时由后端从色板自动挑一个;
   *   已分配但 NULL 的老数据保持 NULL → 事件块回落到教室颜色 (改动 2 的回落逻辑)。
   */
  colorFor(userId) {
    const c = this.colorDraft[userId];
    return c || null;
  }

  /** 点色块: 记录草稿 + 收起色盘 + redraw (即时反映到本行头像/边框) */
  pickColor(userId, hex) {
    this.colorDraft[userId] = hex;
    // v0.10.1 — 给颜色 = 打算让这个人在本校区带课, 没分配的话这个颜色根本无处可存
    //   (coach_color 挂在 user_school 行上, 没分配就没有行)。
    //   【实测根因, 不是猜的】改前 pickColor 只写草稿, 不碰 checkedIds;
    //   submit() 的三分支 (新分配/移除/已分配改色) 对"未分配+未勾选+有草稿"一条都不命中,
    //   于是提交时这个人的 ops 为空 —— 颜色 100% 丢失, 且 UI 上完全没有报错或提示。
    //   列表里最后一行往往正是"新加进来、还没分配"的人, 所以现象表现为"最后一个人生效不了"。
    //   实测 (V 测): 给最后一行 uid=9029 选红 → 保存 → API 里没有这个人的记录。
    //   这里改成: 选色即视为分配, 顺手勾上, 让"改前保留 / 改后生效"两种路径归一。
    //   ⚠️ 这是有可见副作用的取舍 (会把这个教练加进本校区), 已写进 v0.10.1 报告请辉哥确认。
    if (!this.checkedIds.has(userId)) {
      this.checkedIds.add(userId);
    }
    // v0.8.10a — 改前只置 openPaletteUserId = null; 改后还要把 body 上的色盘一起卸载
    this.closePalette();
  }

  /**
   * v0.8.10a — 色盘 z-index: 取页面上 modal 层 (`.ModalManager` / `.Modal-backdrop`)
   *   最大的 computed z-index 再 +1, 保证浮在 modal 之上 (Flarum --zindex-modal 默认 1050)。
   *   不写死 1051: 换主题改了 --zindex-modal 也不会被压回去。
   */
  paletteZIndex() {
    let max = 1000;
    document.querySelectorAll('.ModalManager, .Modal-backdrop').forEach(el => {
      const z = parseInt(window.getComputedStyle(el).zIndex, 10);
      if (!Number.isNaN(z) && z > max) max = z;
    });
    return max + 1;
  }

  /** v0.8.10a — 卸载 body 上的色盘 (幂等: 没开色盘时什么都不做) */
  closePalette() {
    const had = this.paletteHost || this.openPaletteUserId !== null;
    if (this.paletteHost) {
      // 纯 DOM 建的色盘, 直接摘节点即可 (没有 vdom 需要清)
      this.paletteHost.remove();
      this.paletteHost = null;
      this.paletteEl = null;
    }
    if (this.onPaletteViewportChange) {
      window.removeEventListener('resize', this.onPaletteViewportChange);
      // scroll 用捕获阶段注册的, 移除时参数 (capture) 必须一致, 否则移不掉
      window.removeEventListener('scroll', this.onPaletteViewportChange, true);
      this.onPaletteViewportChange = null;
    }
    this.openPaletteUserId = null;
    if (had) m.redraw();
  }

  /**
   * v0.8.10a — 色盘跟随锚点重定位
   *
   * 列表滚动 / 窗口 resize 后色块会移动, 坐标必须跟着重算 (改前色盘是行内 absolute,
   * 位置由 CSS 自己算, 天然跟随; 改成 body 上的 fixed 之后就得手动跟)。
   */
  repositionPalette() {
    if (!this.paletteEl || !this.paletteAnchorEl || !this.paletteAnchorEl.isConnected) return;
    const anchor = this.paletteAnchorEl.getBoundingClientRect();
    const size = this.paletteEl.getBoundingClientRect();
    const pos = computePalettePos(anchor, {
      w: size.width,
      h: size.height
    }, {
      w: window.innerWidth,
      h: window.innerHeight
    });
    this.paletteEl.style.left = pos.left + 'px';
    this.paletteEl.style.top = pos.top + 'px';
  }

  /**
   * 点色块本体: 展开/收起这一行的 10 色盘 (不进 checkedIds, 不影响分配勾选)
   *
   * v0.8.10a — 改前: 只切 openPaletteUserId, 色盘由 content() 渲染在**行内** (absolute),
   *   最后一行会掉进 modal footer 里点不到 (桌面 5/10, 移动 0/10)。
   *   改后: 色盘挂 document.body (position:fixed), 位置按锚点实时算, 下方不够自动向上翻;
   *         色块 DOM 不动 (mithril 复用同一节点) → 锚点一直有效, 滚动/resize 都能跟上。
   */
  togglePalette(userId) {
    if (this.openPaletteUserId === userId) {
      this.closePalette();
      return;
    }
    // 先关掉可能开着的另一个 (同一时刻只允许一个色盘)
    this.closePalette();
    const anchorEl = document.querySelector('.AssignStaffModal .ZS-assign-user-row[data-uid="' + userId + '"] .ZS-assign-color-swatch');
    if (!anchorEl) {
      // 兜底: 找不到锚点就只切状态, 不会出现"开了却看不见"的色盘
      this.openPaletteUserId = userId;
      m.redraw();
      return;
    }
    const {
      host,
      palette
    } = buildPaletteDom({
      currentHex: this.colorFor(userId),
      onPick: hex => this.pickColor(userId, hex)
    });
    // z-index 写在挂载点 (host) 上而不是色盘本体: host 自己就是 fixed + z-index 的层叠上下文,
    // 写在色盘上只会在这个上下文内部排序, 抬不过 modal 层。
    host.style.zIndex = this.paletteZIndex();
    document.body.appendChild(host);
    this.paletteHost = host;
    this.paletteEl = palette;
    this.paletteAnchorEl = anchorEl;
    this.openPaletteUserId = userId;
    // 挂上去 (尺寸已经是最终值) 再定位: 直接量真实尺寸, 不把色盘宽高硬编码进 JS
    this.repositionPalette();
    // 跟随滚动/resize
    this.onPaletteViewportChange = () => this.repositionPalette();
    window.addEventListener('resize', this.onPaletteViewportChange);
    window.addEventListener('scroll', this.onPaletteViewportChange, true);
    m.redraw();
  }
  async submit() {
    this.saving = true;
    this.error = '';
    m.redraw();
    const currentRoleAssigned = new Set(this.assigned.filter(u => u.role === this.activeRole).map(u => u.user_id));
    // v0.8.10 — 后端已存的颜色, 用来判断"颜色是不是真的改了"
    const savedColorById = {};
    this.assigned.forEach(u => {
      savedColorById[u.user_id] = u.coach_color || null;
    });
    const ops = [];
    // ⚠️ v0.10.1 — 遍历口径从 this.filteredUsers() 改成"当前角色的全部候选"。
    //   改前遍历的是**搜索框过滤后**的列表: 一旦用户搜了关键字, 没被搜到的人整个从
    //   ops 里消失 —— 他之前改好的颜色既不会生成 op, 也不会被回滚, 而是静默丢失。
    //   实测 (V 测): 给 Kevin(uid 135) 选粉 → 搜索 "Ziven" → 保存 → API 仍是旧色。
    //   颜色是**每人一份的全局草稿**, 跟当前看到哪几行无关, 所以提交口径必须是全集。
    const roleCandidates = this.candidates.filter(u => u.role === this.activeRole);
    for (const u of roleCandidates) {
      const wasAssigned = currentRoleAssigned.has(u.id);
      const isChecked = this.checkedIds.has(u.id);
      const color = this.colorFor(u.id);
      if (!wasAssigned && isChecked) {
        // 新分配: 带了颜色就按用户选的, 没带就传 null 让后端从色板挑一个没被用过的
        ops.push({
          user_id: u.id,
          action: 'add',
          coach_color: color
        });
      } else if (wasAssigned && !isChecked) {
        ops.push({
          user_id: u.id,
          action: 'remove'
        });
      } else if (wasAssigned && isChecked && color !== savedColorById[u.id]) {
        // v0.8.10 — 只改颜色、勾选没动的情况 (改前这里什么都不发, 改色永远存不进去)
        //   后端 UpdateSchoolStaffController 对"已存在 + add + 合法 coach_color"走的是改色分支,
        //   不会重复建行, 也不会把 added 计数算进去。
        ops.push({
          user_id: u.id,
          action: 'add',
          coach_color: color
        });
      }
      // v0.10.1 — "未分配 + 未勾选 + 却有颜色草稿" 这条组合改前一条 op 都不生成,
      //   颜色直接被丢掉 (这就是"最后一个人的颜色保存后没生效")。
      //   修法不在 submit 里补一条 op, 而是在 pickColor 里就把它勾上 (见该方法注释):
      //   颜色只存在于 user_school 行上, 没分配就没有行可写, 必然无处可存。
      //   所以这里留空分支只为说明, 不再有"未分配未勾选却带色"的可达状态。
    }
    if (ops.length === 0) {
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb();
      this.hide();
      return;
    }
    try {
      const resp = await app.request({
        method: 'PUT',
        url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/schools/' + this.school.id + '/staff',
        body: {
          data: {
            attributes: {
              ops
            }
          }
        },
        serialize: x => JSON.stringify(x),
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // v0.10.2 (修 P1-2) — 后端发现有合同的成员没移出时, 会在 200 里回 blocked +
      //   blocked_message (不 throw, 以免连带影响同批其他 op)。这里必须**留在 modal 里**
      //   并把原因显示出来 —— 改前是直接 hide(), 用户看不到任何东西, 刷新后发现人还在,
      //   只会以为系统出 bug。
      const blockedMsg = resp && resp.blocked_message;
      if (blockedMsg) {
        this.saving = false;
        this.error = blockedMsg;
        m.redraw();
        return;
      }
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb();
      this.hide();
    } catch (e) {
      this.saving = false;
      this.error = '保存失败: ' + (e?.message || '');
      console.error('[AssignStaff] save failed', e);
      m.redraw();
    }
  }
  setRole(role) {
    // v0.8.10a — 切角色会把整个列表换掉, 色盘的锚点随行一起消失 → 先收色盘,
    //   否则色盘会变成没有归属的孤儿浮在 modal 上 (改前是跟着列表一起被重绘掉, 不会留)
    this.closePalette();
    this.activeRole = role;
    m.redraw();
  }

  // v0.18.24 — 角色 tab (辉哥 10/20 原话: 「还有下面3个tab中的文字, 去掉候选人数量文字,
  //   只展示与[已]分配数量, 并且tab里的文字能不能上下分两行展示」)
  //   【为什么统一成这个方法】改前三个 tab 各写各的 —— coach 用 content() 里的变量,
  //   sales / manager 直接内联 filter(...) —— 同一个意思三种写法, 改文案必漏一个。
  //   现在三个 tab 全部走这里, 文案/结构只有一份。
  //   【结构】每格上下两行: 第一行 = 图标 + 角色名; 第二行 = 只显示「已分配数量」。
  //   【为什么去掉候选人数】「N 候选人」是**全量候选**数, 跟用户此刻的操作无关 ——
  //   占了最显眼的位置却信息量最低; 「N 已分配」才是选人界面里唯一有决策价值的数。
  //   【数量为 0】照常显示「0 人已分配」, 但加 .zero 类 → LESS 把数字染灰,
  //   这样三个 tab 的行数/高度完全一致, 切 tab 时不跳。
  roleTab(role, label, icon) {
    const assignedCount = this.assigned.filter(u => u.role === role).length;
    const active = this.activeRole === role ? ' active' : '';
    const zero = assignedCount === 0 ? ' zero' : '';
    return m("div", {
      className: 'ZS-assign-role-tab' + active + zero,
      onclick: () => this.setRole(role)
    }, m("div", {
      className: "ZS-assign-role-l1"
    }, m("i", {
      className: 'fas ' + icon
    }), label), m("div", {
      className: "ZS-assign-role-l2"
    }, m("b", null, assignedCount), " \u4EBA\u5DF2\u5206\u914D"));
  }
  content() {
    const users = this.filteredUsers();
    const assignedIds = new Set(this.assigned.map(u => u.user_id));
    const checkedCount = users.filter(u => this.checkedIds.has(u.user_id)).length;
    return m("div", null, m("div", {
      className: "Modal-body"
    }, m("div", {
      style: "font-size:12px;color:var(--zs-text-2-8d96a3, #8d96a3);margin-bottom:14px;"
    }, "\u4ECE\u540E\u53F0\u914D\u7F6E\u7684\u6559\u7EC3\u7EC4/\u9500\u552E\u7EC4/\u5E97\u957F\u7EC4\u4E2D\u5206\u914D, \u5DF2\u5206\u914D\u7684\u4EBA\u4F1A\u51FA\u73B0\u5728\u6821\u533A\u5361\u7247\u4E2D"), m("div", {
      className: "ZS-assign-role-bar"
    }, this.roleTab('coach', '教练', 'fa-person-chalkboard'), this.roleTab('sales', '销售', 'fa-briefcase'), this.roleTab('manager', '店长', 'fa-user-tie')), m("div", {
      style: "position:relative;margin-bottom:12px;"
    }, m("i", {
      className: "fas fa-magnifying-glass",
      style: "position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--zs-text-2-8d96a3, #8d96a3);font-size:13px;"
    }), m("input", {
      type: "text",
      placeholder: "\u641C\u7D22\u7528\u6237\u540D/\u90AE\u7BB1...",
      value: this.search
      // v0.8.10a — 搜索会重排/过滤列表, 锚点行可能消失 → 先收色盘 (跟 setRole 同理)
      ,
      oninput: ev => {
        this.search = ev.target.value;
        this.closePalette();
        m.redraw();
      },
      style: "width:100%;padding:9px 12px 9px 36px;background:var(--zs-surface-4-0e1116, #0e1116);border:1px solid var(--zs-overlay-1-255-255-255-008, rgba(255,255,255,0.08));border-radius:8px;color:var(--zs-text-1-e8ecf1, #e8ecf1);font-size:13px;box-sizing:border-box;"
    })), this.loading ? m("div", {
      style: "padding:60px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u52A0\u8F7D\u4E2D...") : m("div", {
      className: "ZS-assign-user-list"
    }, users.length === 0 && m("div", {
      style: "padding:60px;text-align:center;color:var(--zs-text-2-9ca3af, #9ca3af);"
    }, "\u6CA1\u6709 ", this.activeRole === 'coach' ? '教练' : this.activeRole === 'manager' ? '店长' : '销售', " \u5019\u9009\u4EBA (\u8BF7\u5728\u540E\u53F0\u914D\u7F6E\u7528\u6237\u7EC4\u6620\u5C04)"), users.map(u => {
      const checked = this.checkedIds.has(u.id);
      const wasAssigned = assignedIds.has(u.id);
      // v0.8.10 — 这一行的生效色 (草稿优先, 回落后端已存值; 没有则 null)
      //   用于头像描边 + 整行左侧色条, 改色后立刻可见 (验收第 1 条: 即时反映)。
      const rowColor = this.colorFor(u.id);
      // v0.6.63 — Flarum Avatar 组件 (辉哥 9/30 22:35 拍板)
      //   store 有 user model → 真实头像 <img src=avatarUrl> / 没 avatar → 首字母 + user.color() 算背景
      //   store 没 user model → fallback 字母头像 (OperationLogsTab v0.6.3e 同款, 防止 TypeError)
      //   Flarum Avatar 不渲染 <a>, 点击不跳转用户页 (符合辉哥需求)
      const userModel = app.store.getById('users', u.id);
      const fallbackLetter = (u.display_name || u.username || '?').charAt(0).toUpperCase();
      return m("div", {
        className: 'ZS-assign-user-row ' + (checked ? 'checked' : ''),
        key: u.id
        // v0.8.10a — data-uid: 色盘 portal 找锚点用 (togglePalette 里按它查色块)
        ,
        "data-uid": u.id
        // v0.8.10 — 改前整行没有任何颜色线索; 改后有色时左侧压一条 3px 色条
        ,
        style: rowColor ? {
          boxShadow: 'inset 3px 0 0 0 ' + rowColor
        } : null,
        onclick: () => this.toggleUser(u.id)
      }, m("div", {
        className: 'ZS-assign-user-avatar ' + this.activeRole
        // v0.8.10 — 头像描边跟随教练色 (跟右边色块同色, 一眼对应)
        ,
        style: rowColor ? {
          boxShadow: '0 0 0 2px ' + rowColor
        } : null
      }, userModel ? m(Avatar, {
        user: userModel
      }) : m("span", {
        className: "Avatar-letter",
        style: "display:flex;align-items:center;justify-content:center;width:100%;height:100%;background:var(--zs-surface-1-374151, #374151);color:var(--zs-text-1-e5ecf1, #e5ecf1);border-radius:50%;font-weight:600;"
      }, fallbackLetter)), m("div", {
        style: "flex:1;min-width:0;"
      }, m("div", {
        style: "font-size:14px;font-weight:500;"
      }, u.display_name || u.username), m("div", {
        style: "font-size:11px;color:var(--zs-text-2-8d96a3, #8d96a3);"
      }, u.email || '', u.email && ' · ', u.username)), wasAssigned && m("span", {
        style: {
          fontSize: 10,
          padding: '2px 6px',
          borderRadius: 4,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          background: 'var(--zs-success-soft-52-199-89-018, rgba(52,199,89,0.18))',
          color: 'var(--zs-success-34c759, #34c759)'
        }
      }, m("i", {
        className: "fas fa-check"
      }), " \u5DF2\u5206\u914D"), m("div", {
        className: "ZS-assign-user-color",
        onclick: e => e.stopPropagation()
      }, m("button", {
        type: "button",
        className: "ZS-assign-color-swatch",
        title: rowColor ? '点击更换颜色: ' + rowColor : '点击预设颜色 (不选则分配时自动分配)',
        onclick: e => {
          e.stopPropagation();
          this.togglePalette(u.id);
        }
      }, rowColor ? m("span", {
        className: "ZS-assign-color-chip",
        style: {
          background: rowColor
        }
      }) : m("i", {
        className: "fas fa-palette",
        style: "color:var(--zs-text-2-8d96a3, #8d96a3);font-size:12px;"
      }))), m("div", {
        className: "ZS-assign-user-check"
      }, checked && m("i", {
        className: "fas fa-check"
      })));
    })), this.error && m("div", {
      className: "ZS-newversion-error",
      style: "margin-top:8px;"
    }, m("i", {
      className: "fas fa-triangle-exclamation",
      style: "color:var(--zs-danger-ef4444, #ef4444);margin-right:6px;"
    }), this.error)), m("div", {
      className: "Modal-footer"
    }, m("span", {
      style: {
        fontSize: 12,
        color: 'var(--zs-text-2-8d96a3, #8d96a3)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6
      }
    }, m("i", {
      className: "fas fa-circle-check",
      style: {
        color: 'var(--zs-brand-2d7be5, #2d7be5)'
      }
    }), " \u5DF2\u9009 ", m("strong", {
      style: {
        color: 'var(--zs-brand-2d7be5, #2d7be5)'
      }
    }, checkedCount), " \u4EBA (\u672C\u6B21\u64CD\u4F5C)"), m("div", {
      style: {
        flex: 1
      }
    }), m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, m("i", {
      className: "fas fa-xmark"
    }), " \u53D6\u6D88"), m("button", {
      className: "Button Button--primary",
      disabled: this.saving,
      onclick: () => this.submit()
    }, m("i", {
      className: "fas fa-check"
    }), " ", this.saving ? '保存中...' : '保存分配')));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/AssignStaffModal', AssignStaffModal);